import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { Yii } from "libraries/interfaces/Yii";
import { LowCostCompanyService } from "app_locobook/service/low-cost-company.service";
import {
    IAirports,
    ICarrier,
    IFareFamilyDictionary,
    IGetAirTripQuote,
    IQuote,
    ISegment
} from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import { IOutSelectQuote } from "app_locobook/pages/quote-page/components/quote-list-container/quote-list-container.component";
import { AirTripResourceService } from "libraries/service/resources/airTrip/airTripResource.service";
import { AllSegmentDialogComponent, IAllSegmentsDialogData } from "app_locobook/shared/all-segment-dialog/all-segment-dialog.component";
import { ISegmentSelect } from "app_locobook/shared/segment-list/all-segment-list.component";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { faCaretRight, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { PriceItemType, ResponseForConvertOfferAfx2jsonType } from '@afidium/ts-facade';
import { INotification, NotificationType } from 'libraries/interfaces/INotification';
import { ILoader } from 'libraries/component/loader/ILoader';
import { IOnAlternativeSelectOutput } from '../fare-family-benchmark/fare-family-benchmark.component';
import { Router } from "@angular/router";
import { IqbaPathSegment } from 'app_locobook/iqba-paths';
import { PriceService } from "../../../../service/price.service";
import { IFareFamily } from "libraries/service/resources/booking/interfaces/IGetBookings";
import { EInterfaceValues } from "libraries/component/interface-flag/interface-flag.component";

class ComponentWording {
    public readonly allSegments: string = Yii.t('wui-iqba-js', 'Tous les vols');
    public readonly book: string = Yii.t('wui-iqba-js', 'Réserver ce voyage');
    public readonly select: string = Yii.t('wui-iqba-js', 'Voir cette proposition');
    public readonly modifySegment: string = Yii.t('wui-iqba-js', "Ajouter à l'itinéraire");
    public readonly showAlternativeFares: string = Yii.t('wui-iqba-js', 'Autres offres');
    public readonly hideAlternativeFares: string = Yii.t('wui-iqba-js', 'Masquer les autres offres');
    public readonly priceChanges: string = Yii.t('wui-iqba-js', 'Le prix total de l\'offre a changé. La modification peut être due à des frais de paiement, à des frais appliqués pour les bébés, ou à des réductions (Low Cost Carriers).');
}

/**
 * Component that displays a single quote in a result list.
 */
@Component({
    selector: 'quote-result',
    template: `
        <div class="quote-marker" #container
             [class.quote-selected]="isSelectedQuote"
             (click)="selectQuote(true)">
            <fa-icon [icon]="iconCaret" *ngIf="isSelectedQuote"></fa-icon>
            <booking [key]="quoteId"
                     [bookingItem]="quote"
                     [originalRequest]="originalRequest"
                     [selectedJourneys]="selectedJourneys"
                     [showFlightDetails]="showFlightDetails"
                     [price]="price"
                     [airports]="airports"
                     [fareFamilyDictionary]="fareFamilyDictionary"
                     [carriers]="carriers"
                     [beginDaysDifference]="quote.beginDaysDifference"
                     [mustEmitPriceWidth]="mustEmitPriceWidth"
                     (onEmitPriceWidth)="onEmitPriceWidth.emit($event)"
                     [higherPriceWidth]="higherPriceWidth">
                <div class="booking-bottom">
                    <btn *ngIf="hasOtherSegments()"
                         click-stop
                         size="small"
                         class="othersFlights"
                         [label]="wording.allSegments"
                         type="actionLink"
                         classList="no-spacing"
                         (onClick)="openAllSegments($event)"></btn>
                    <ng-container *permissionIf="offerAlternativesPermission; airQuote quote">
                        <btn *ngIf="hasAlternativeFares"
                             click-stop
                             class="alternative-fares"
                             [label]="isAlternativeFaresVisible ? wording.hideAlternativeFares : wording.showAlternativeFares"
                             type="actionPrincipal"
                             (onClick)="toggleAlternativeFares($event)">
                        </btn>

                    </ng-container>
                    <btn class="e2e-select-quote-btn"
                         [label]="buttonWording"
                         type="actionPrincipal"
                         [style.visibility]="visibility"
                         (click)="selectedFareFamilyOutput ? selectAlternative($event, selectedFareFamilyOutput): selectQuote()">
                    </btn>

                </div>
            </booking>

            <notification *ngIf="warningMessage"
                          type="warning"
                          class="flex-1">
                {{warningMessage}}
            </notification>


            <ng-container *ngIf="isAlternativeFaresVisible">
                <fare-family-benchmark [sqaId]="tripId"
                                       [quoteId]="quoteId"
                                       [parentWidth]="containerWidth"
                                       [fareFamilyDictionary]="fareFamilyDictionary"
                                       [segments]="selectedSegments"
                                       [selectedJourneys]="selectedJourneys"
                                       [mustRedirectAfterSelection]="mustRedirectAfterSelection"
                                       [sessionPostOnUpdateBooking]="sessionPostOnUpdateBooking"
                                       (onAlternativeOpen)="setSelectedAlternative($event)"
                                       (onAlternativeSelect)="alternativeSelect($event)"></fare-family-benchmark>
            </ng-container>

        </div>
    `,
    styleUrls: ["./quote-result.component.scss"]
})
export class QuoteResultComponent implements OnInit, OnChanges, AfterViewInit {

    public readonly wording: ComponentWording = new ComponentWording();

    @Input()
    public mustRedirectAfterSelection: boolean = true;
    @Input()
    public tripId: string;

    @Input()
    public quoteId: string;

    @Input()
    public sessionPostOnUpdateBooking: any = null;

    @Input()
    public quote: IQuote;

    @Input()
    public isSelectedQuote: boolean;

    @Input()
    public originalRequest: any;

    @Input()
    public airports: IAirports;
    @Input()
    public carriers: ICarrier;
    @Input()
    public fareFamilyDictionary: { [key: string]: IFareFamilyDictionary };

    @Input()
    public isLast: boolean;

    @Input()
    public mustEmitPriceWidth: boolean;
    @Input()
    public higherPriceWidth: number;
    @Input()
    public price: any;

    @Input()
    public isOfferSelection: boolean;
    @Input()
    public vccPermission: boolean;
    @Input()
    public ndcPermission: boolean;
    @Input()
    public isModifySegment: boolean;

    @Output()
    public readonly onEmitPriceWidth: EventEmitter<number> = new EventEmitter<number>();
    @Output()
    public readonly onAlternativeSelect: EventEmitter<IOnAlternativeSelectOutput> = new EventEmitter<IOnAlternativeSelectOutput>();
    @Output()
    public readonly onSelectedQuoteOnly: EventEmitter<IOutSelectQuote> = new EventEmitter<IOutSelectQuote>();
    @Output()
    public readonly onSelectedQuote: EventEmitter<IOutSelectQuote> = new EventEmitter<IOutSelectQuote>();
    @Output()
    public readonly onChooseJourney: EventEmitter<IOutSelectQuote> = new EventEmitter<IOutSelectQuote>();
    @Output()
    public readonly onViewSelectedQuote: EventEmitter<string> = new EventEmitter();
    @Output()
    public readonly onIsReady: EventEmitter<void> = new EventEmitter<void>();
    @Output()
    public readonly onSelectOtherFlight: EventEmitter<IOutSelectQuote> = new EventEmitter<IOutSelectQuote>();

    @ViewChild('container')
    public readonly container: ElementRef;

    public selectedJourneys: string[];
    public selectedSegments: string[];
    public gqaNotification: INotification = {
        active: false,
        type: NotificationType.Error
    };
    public gqaLoader: ILoader = {
        active: false,
        message: Yii.t('wui-iqba-js', "Chargement des autres offres en cours...")
    };
    public fareFamilyBenchmark: ResponseForConvertOfferAfx2jsonType;
    public containerWidth: string;
    public fareFamilyXId: string;
    public quoteHasLcc: boolean;
    public quoteHasNdc: boolean;
    public hasAlternativeFares: boolean;
    public isAlternativeFaresVisible: boolean = false;
    public showFlightDetails: boolean[] = [];
    public visibility: "hidden" | "visible" = "visible";

    public warningMessage: string = null;

    public iconCaret: IconDefinition = faCaretRight;
    public selectedFareFamilyOutput: IOnAlternativeSelectOutput;

    public offerAlternativesPermission: string = 'iqba.quote.offer.alternatives.use';
    public buttonWording: string = this.wording.book;

    constructor(
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly lowCostCompanyService: LowCostCompanyService,
        private readonly airTripResourceService: AirTripResourceService,
        private readonly priceService: PriceService,
    ) {
    }

    public ngOnInit(): void {
        // buttonWording initially set as wording.book, changing it only if needed
        if(this.isOfferSelection || this.isModifySegment){
            this.buttonWording = this.isOfferSelection ? this.wording.select : this.wording.modifySegment;
        }
    }

    public ngOnChanges(): void {
        // setDefaultSelectedJourneys
        if (!this.selectedJourneys) {
            this.selectedJourneys = Object.values(this.quote.segment).map((segment: ISegment) => segment.journey[0]);
        }
        if (!this.selectedSegments) {
            this.selectedSegments = Object.keys(this.quote.segment);
        }
        this.onChooseJourney.emit({quoteId: this.quoteId, selectedJourneys: [...this.selectedJourneys]});
        // has LowCostCompanies
        this.quoteHasLcc = this.lowCostCompanyService.hasLcc(this.quote, this.selectedJourneys);
        this.quoteHasNdc = this.quote.interface === EInterfaceValues.NDC;
        // this.visibility = (this.quoteHasLcc && !this.vccPermission) || (!this.ndcPermission && this.quoteHasNdc) ? 'hidden' : 'visible';
        this.visibility = (this.quoteHasLcc && !this.vccPermission)  ? 'hidden' : 'visible';
        this.hasAlternativeFaresChecking();
        if (this.hasAlternativeFares) {
            if(Object.keys(this.quote?.fareFamily).length > 0){
                const key: string = Object.keys(this.quote?.fareFamily)[0];
                const value: IFareFamily = Object.values(this.quote.fareFamily)[0];
                this.fareFamilyXId = value.syscode && value.syscode.startsWith('FF') ? value.syscode : key;
            }else{
                this.fareFamilyXId = null;
            }

        }
    }

    public ngAfterViewInit(): void {
        if (this.isLast) {
            this.onIsReady.emit();
        }
    }

    public alternativeSelect($event: IOnAlternativeSelectOutput): void {
        this.onAlternativeSelect.emit($event);
    }

    public hasOtherSegments(): boolean {
        return !!Object.values(this.quote.segment)
            .find((segment: ISegment) => segment.journey.length > 1);
    }

    public openAllSegments(event: Event): void {
        //avoid select quote
        event.stopPropagation();
        this.showFlightDetails = [];
        this.airTripResourceService.getQuote(this.tripId, this.quoteId)
            .subscribe((quote: IGetAirTripQuote) => {
                const data: IAllSegmentsDialogData = {
                    quoteId: this.quoteId,
                    segmentList: quote.booking[this.quoteId].segment,
                    selectedSegment: this.selectedJourneys,
                };
                const dialog: MatDialogRef<AllSegmentDialogComponent, ISegmentSelect> = this.dialog.open(AllSegmentDialogComponent, {
                    data,
                });
                dialog.afterClosed().subscribe((selectedSegmentInfo: ISegmentSelect) => {
                    if (selectedSegmentInfo) {
                        this.selectedJourneys = selectedSegmentInfo.selectedSegment;
                        this.onChooseJourney.emit({quoteId: this.quoteId, selectedJourneys: this.selectedJourneys});
                        this.onSelectOtherFlight.emit({quoteId: this.quoteId, selectedJourneys: this.selectedJourneys});
                    }
                });
            });
    }

    public async selectAlternative($event: MouseEvent, selectedAlternativeOutput: IOnAlternativeSelectOutput): Promise<void> {
        event.stopPropagation();
        await this.router
            .navigate([
                IqbaPathSegment.Locobook,
                IqbaPathSegment.Search,
                this.tripId,
                this.quoteId,
                IqbaPathSegment.Alternative,
                selectedAlternativeOutput.offerAlternativePriceXId])
            .then((): void => {
                this.onAlternativeSelect.emit(selectedAlternativeOutput);
            });
    }

    public selectQuote(only?: boolean): boolean {

        if (!this.quoteHasLcc || (this.quoteHasLcc && this.vccPermission)) {
            const quoteAndJourneys: IOutSelectQuote = {
                quoteId: this.quoteId,
                selectedJourneys: this.selectedJourneys
            };
            if (only) {
                this.onSelectedQuoteOnly.emit(quoteAndJourneys);
            } else {
                this.onSelectedQuote.emit(quoteAndJourneys);
            }
            return true;
        }
        return false;
    }

    public viewQuote(): boolean {
        this.onViewSelectedQuote.emit(this.quoteId);
        return true;
    }

    public toggleAlternativeFares(event: Event): void {
        event.stopPropagation();
        this.containerWidth = `${this.container?.nativeElement?.offsetWidth}px`;
        this.isAlternativeFaresVisible = !this.isAlternativeFaresVisible;
    }

    public setSelectedAlternative($event: IOnAlternativeSelectOutput): void {
        this.selectedFareFamilyOutput = $event;

        const initPrice: number = this.priceService.getTotalPrice(this.quote.total)?.cost;
        let newPrice: number = 0;
        if(this.selectedFareFamilyOutput?.quote?.offer?.total?.cost?.totalArray){
            newPrice = this.selectedFareFamilyOutput.quote.offer.total.cost.totalArray
                .find((totalItem: PriceItemType): boolean => !Boolean(totalItem.xReason))
                .currencyArray[0].value;
        }
        this.warningMessage = (initPrice !== newPrice ? this.wording.priceChanges : null);
    }
    private hasAlternativeFaresChecking(): void {
        this.hasAlternativeFares = Boolean(this.quote?.fareFamily);
    }
}
